/**
 * Leave balance helpers.
 *
 * F&O's EssLeaveBalances.BalanceAvailable is stored per full-time worker; the
 * balance an employee actually has is scaled by the FullTimeEquivalent (FTE) of
 * their active position(s) from PositionsV2:
 *
 *   balance = FullTimeEquivalent * BalanceAvailable
 */

const DEFAULT_FTE = 1;

const isActiveNow = (position, now) => {
  const from = Date.parse(position.DetailEffective);
  const to = Date.parse(position.DetailExpiration);
  const assignmentEnd = Date.parse(position.WorkerAssignmentEnd);
  if (!Number.isNaN(from) && from > now) return false;
  if (!Number.isNaN(to) && to <= now) return false;
  if (!Number.isNaN(assignmentEnd) && assignmentEnd <= now) return false;
  return true;
};

/**
 * Total FTE across the worker's currently active positions.
 * Falls back to 1 (no scaling) when there is no usable position data, so a
 * missing/failed position lookup never zeroes out a balance.
 */
export const getActiveFte = (positions = [], now = Date.now()) => {
  const fteValues = positions
    .filter((p) => isActiveNow(p, now))
    .map((p) => Number(p.FullTimeEquivalent))
    .filter((fte) => Number.isFinite(fte) && fte > 0);

  if (fteValues.length === 0) return DEFAULT_FTE;
  return fteValues.reduce((sum, fte) => sum + fte, 0);
};

/**
 * FTE-adjusted balance, or null when BalanceAvailable is missing.
 */
export const getAdjustedBalance = (balanceAvailable, fte = DEFAULT_FTE) => {
  if (balanceAvailable == null) return null;
  const value = Number(balanceAvailable);
  return Number.isFinite(value) ? value * fte : null;
};
